/**
 * PlanActivationHandoff.jsx — RC 1.44.O1.B
 *
 * Intermediate trust screen between plan activation (Base OR Paid)
 * and first dashboard paint. Forces a clean logout + re-login so the
 * new entitlement hydrates from a fresh session, not a stale token.
 *
 * Flow:
 *   1. OnboardingPlanPage (Base) OR OnboardingPaymentPage (Paid)
 *      detects a successful activation.
 *   2. Both routes `navigate('/plan-activated', { replace: true })`.
 *   3. This page renders the "Your plan is ready" screen with a
 *      single CTA.
 *   4. CTA click → `signOut()` → `/login?just_signed_up=1`.
 *
 * Route guard: Inside <ProtectedRoute requireProfile={true}> but
 * OUTSIDE the <EntitlementGate> shell — the entitlement is fresh
 * and active at this point but the gate should not interrupt the
 * handoff screen (same rule the /onboarding/plan + /onboarding/payment
 * routes follow).
 *
 * Copy is operator-specified verbatim — do not reword.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2 } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import LegalFooter from '@/components/LegalFooter';
import { track } from '@/lib/analytics';


export default function PlanActivationHandoff() {
  const { signOut } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    track('plan_handoff_seen');
  }, []);

  const handleSignInAgain = async () => {
    if (submitting) return;
    setSubmitting(true);
    track('plan_handoff_cta_clicked');
    // 1. Drop the Supabase session server-side and locally. Fire-and-
    //    forget on error — the local session is invalidated regardless.
    try {
      await signOut();
    } catch {
      /* swallow: full page reload below still clears client state */
    }
    // 2. Full page navigation to /login?just_signed_up=1.
    //
    //    Why `window.location.replace` instead of `navigate`:
    //      • React Router `navigate` from inside a still-mounted
    //        authenticated tree loses the query param to a race with
    //        <ProtectedRoute>'s `<Navigate to="/login">` on the
    //        SIGNED_OUT event.
    //      • Navigating BEFORE signOut hits <PublicRoute>'s redirect
    //        to /post-login (authenticated users can't sit on /login).
    //      • `window.location.replace` forces a full document reload,
    //        tearing down React state, clearing any in-memory plan /
    //        entitlement caches, and rehydrating from a cold
    //        AuthContext. That matches the Slice B mandate of
    //        "clean state hydration" exactly.
    window.location.replace('/login?just_signed_up=1');
  };

  return (
    <div
      className="flex flex-col min-h-screen bg-ndor-canvas"
      data-testid="plan-activation-handoff-page"
    >
      <header className="w-full px-6 py-5 sm:px-10 sm:py-6 flex items-center gap-3">
        <BrandLogo variant="symbol" size={36} />
        <span className="text-[15px] font-serif-display font-semibold tracking-tight text-ndor-ink">
          NDOR<span className="text-gold-accent">Flow</span>
        </span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <div
            className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600"
            data-testid="plan-activation-handoff-badge"
          >
            <CheckCircle2 className="h-7 w-7" />
          </div>

          <h1
            className="text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink"
            data-testid="plan-activation-handoff-headline"
          >
            Your plan is ready.
          </h1>

          <p
            className="mt-4 text-base text-ndor-ink-secondary font-manrope"
            data-testid="plan-activation-handoff-body"
          >
            Sign in again to activate your updated dashboard securely.
          </p>

          <div className="mt-8">
            <Button
              size="lg"
              onClick={handleSignInAgain}
              disabled={submitting}
              data-testid="plan-activation-handoff-cta"
              className="w-full sm:w-auto min-w-[220px] font-medium"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting ? 'Signing you out…' : 'Sign in again →'}
            </Button>
          </div>

          <p className="mt-6 text-xs text-ndor-ink-subtle font-manrope">
            We sign you out for your security. A fresh session makes sure your
            new plan and features are loaded without leftover state.
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
